import * as ibantools from 'ibantools';
import * as yup from 'yup';
import { lowerFirst, round, sumBy } from 'lodash';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  type BaseCreateFormValues,
  type BaseUpdateFormValues,
  type LineItem,
  type OctopusJournalKey,
  type ShareValues,
} from '../../shared/types/formTypes';
import { type BicValueKey } from '~/common/enums/bicValues';
import { type InboundHoaInvoice } from '~/common/types/finance/inbound/hoa/inboundHoaInvoices';

import { DivisionType, InboundHoaInvoiceType, PaymentMethod } from '~/common/enums';

import { canBeOGM, isValidOGM } from '@/shared/utils/ogm';
import { formatAsCurrency } from '~/common/utils/numberUtils';
import { roundTo } from '@/shared/utils/helpers';

import foldDeferredCostLines from '../../shared/utils/foldDeferredCostLines';
import useDayjs from '@/shared/hooks/useDayjs';

export const isUpdateFormValues = (value: BaseCreateFormValues): value is BaseUpdateFormValues =>
  !!(value as BaseUpdateFormValues).invoiceId;

export const validateIndividualDivision = (
  lineItem: Pick<LineItem, 'divisionKey' | 'grossAmount' | 'shares'>,
) => {
  const { divisionKey, grossAmount, shares } = lineItem;
  const { t } = i18next;

  const errors: string[] = [];

  if (divisionKey)
    return {
      errors,
      isValid: true,
    };

  const relationsMissing = shares?.some((share) => !share.buildingRelationId);

  const sharesTotal = sumBy(shares, (share) => {
    const { amount } = share;

    return amount || 0;
  });

  const totalAmount = grossAmount || 0;

  const hasRemainingAmount = totalAmount.toFixed(2) !== sharesTotal.toFixed(2);
  const remainingAmount = totalAmount - sharesTotal;

  if (relationsMissing) {
    errors.push(t('errors:relationsMissing'));
  }
  if (hasRemainingAmount) {
    errors.push(
      t('_inboundHoaInvoice.actions.amountToDivide', {
        amountLeftToDivide: formatAsCurrency(remainingAmount),
        totalAmountToDivide: formatAsCurrency(totalAmount),
      }),
    );
  }
  if (!totalAmount) {
    errors.push(t('errors:totalAmountMissing'));
  }

  return {
    errors,
    isValid: !errors.length,
  };
};

const DECIMALS = 2;

const useInvoiceValidationFormikContext = (invoice?: InboundHoaInvoice) => {
  const { t } = useTranslation(['errors', 'common']);
  const { dayjs } = useDayjs();

  const initialValues = (() => {
    if (invoice) {
      const initialBic = invoice?.supplierCompanyRelation.iban.bic;

      const initialLineItems = foldDeferredCostLines(invoice.lineItems).map((lineItem) => ({
        ...lineItem,
        divisionType: DivisionType.Amount,
        isDefault: false,
        percentage:
          invoice.grossAmount && lineItem.grossAmount
            ? round((lineItem.grossAmount / invoice.grossAmount) * 100, DECIMALS)
            : null,
        shares: (lineItem.shares as ShareValues[]).map((share) => {
          const totalShares = sumBy(lineItem.shares, (s) => s.share ?? 0);
          const amount = roundTo(((share.share ?? 0) / totalShares) * lineItem.grossAmount, 2);
          const fraction = totalShares ? (share.share ?? 0) / totalShares : 0;
          const percentage = roundTo(fraction * 100, 3);
          return {
            ...share,
            amount,
            percentage,
          };
        }),
        vatAmount: (lineItem.grossAmount || 0) - (lineItem.netAmount || 0),
      }));

      const isDraft = [
        InboundHoaInvoiceType.Draft,
        InboundHoaInvoiceType.DraftPurchaseInvoice,
        InboundHoaInvoiceType.DraftCreditNote,
      ].includes(invoice.type);

      return {
        ...invoice,
        amountToPay: invoice.amountToPay ?? 0,
        bookYearId: invoice.bookYearId,
        bookYearPeriod: invoice.bookYearPeriod,
        comment: invoice.comment,
        creditNotePurchaseInvoiceId: null,
        creditNotePurchaseInvoiceSelect: null,
        document: null,
        draftType: isDraft ? invoice.draftType : null,
        homeownerAssociationId: invoice.homeownerAssociationRelation.homeownerAssociationId,
        initialOctopusJournalKey: invoice.octopusJournalKey,
        invoiceDivision: null,
        invoiceId: invoice.id,
        isDraft,
        lineItems: initialLineItems,
        octopusJournalKey: null,
        paymentMethod: Object.values(PaymentMethod).includes(invoice.paymentMethod)
          ? invoice.paymentMethod
          : null,
        supplierAccountNumber: invoice.supplierCompanyRelation.iban.accountNumber,
        supplierBic:
          !!initialBic && initialBic !== 'Unspecified' ? (initialBic as BicValueKey) : null,
        supplierCompanyId: invoice.supplierCompanyRelation.companyId,
        supplierSelect: invoice.supplierCompanyRelation.companyId
          ? {
              label: invoice.supplierCompanyRelation.name,
              value: invoice.supplierCompanyRelation.companyId,
            }
          : null,
      };
    }

    return {
      amountToPay: null,
      bookYearId: null,
      bookYearPeriod: null,
      caseReference: null,
      comment: null,
      creditNotePurchaseInvoiceId: null,
      description: null,
      descriptions: [],
      document: null,
      draftType: null,
      dueDate: null,
      grossAmount: null,
      homeownerAssociationId: null,
      initialOctopusJournalKey: null,
      invoiceDate: null,
      invoiceDivision: null,
      invoiceNumber: null,
      isDraft: false,
      isPaymentBlocked: false,
      lineItems: [],
      meterCode: null,
      octopusJournalKey: null,
      paymentMethod: PaymentMethod.WireTransfer,
      structuredReference: null,
      supplierAccountNumber: null,
      supplierBic: null,
      supplierCompanyId: null,
      supplierSelect: null,
      type: null,
    };
  })();

  const initialTouched = {
    amountToPay: (initialValues?.amountToPay || 0) < 0,
    grossAmount: (initialValues?.grossAmount || 0) < 0,
    paymentMethod: !!invoice && !!initialValues.paymentMethod,
    supplierAccountNumber: !!initialValues.supplierAccountNumber,
  };

  const requireForFinal = <T extends () => yup.BaseSchema>(schemaBuilder: T): ReturnType<T> =>
    schemaBuilder()
      .nullable()
      .test('requireForFinal', t('fieldIsRequired'), (value: any, context: any) => {
        if (context.parent.isDraft || context.options.context?.isDraft) {
          return true;
        }
        return value != null;
      });

  const requireForNew = <T extends () => yup.BaseSchema>(schemaBuilder: T): ReturnType<T> =>
    schemaBuilder()
      .nullable()
      .when('invoiceId', {
        is: (invoiceId: string | null) => !invoiceId,
        then: schemaBuilder().nullable().required(t('fieldIsRequired')),
      });

  const requireForInvoice = <T extends () => yup.BaseSchema>(schemaBuilder: T): ReturnType<T> =>
    schemaBuilder()
      .nullable()
      .when(['isDraft', 'octopusJournalKey'], {
        is: (isDraft: boolean, octopusJournalKey: OctopusJournalKey | null) =>
          !isDraft && octopusJournalKey?.type !== InboundHoaInvoiceType.CreditNote,
        then: schemaBuilder().nullable().required(t('fieldIsRequired')),
      });

  const grossAmountSchema = requireForFinal(yup.number)
    .transform((value) => (Number.isNaN(value) ? null : value))
    .test('maxAmountForLinkedPurchaseInvoice', '', (value, { parent, createError }) => {
      if (
        !parent.creditNotePurchaseInvoiceSelect ||
        parent.octopusJournalKey?.type !== InboundHoaInvoiceType.CreditNote
      ) {
        return true;
      }

      const max = parent?.creditNotePurchaseInvoiceSelect?.grossAmount || 0;
      const valid = (value || 0) <= max;

      if (valid) return true;

      return createError({
        message: t('maxValueX', {
          val: formatAsCurrency(parent.creditNotePurchaseInvoiceSelect.grossAmount),
        }),
        path: 'grossAmount',
      });
    });

  const octopusJournalKeySchema = (() => {
    const baseSchema = () =>
      yup.object({
        key: requireForFinal(yup.string),
        type: requireForFinal(yup.string),
        value: requireForFinal(yup.string),
      });

    return requireForFinal(baseSchema);
  })();

  const validationSchema = yup.object({
    amountToPay: requireForInvoice(yup.number)
      .transform((value) => (Number.isNaN(value) ? null : value))
      .test(
        'amountMustBePositive',
        t('fieldXmustBePositive', { field: lowerFirst(t('common:amountToPay')) }),
        (value) => {
          if ((value || 0) < 0) {
            return false;
          }
          return true;
        },
      ),
    bookYearId: requireForFinal(yup.string),
    bookYearPeriod: requireForFinal(yup.number),
    comment: yup.string().nullable(),
    creditNotePurchaseInvoiceId: yup.string().nullable(),
    description: yup.string().nullable(),
    document: requireForNew(yup.mixed),
    dueDate: requireForFinal(yup.string),
    grossAmount: grossAmountSchema,
    homeownerAssociationId: requireForFinal(yup.string),
    invoiceDate: requireForFinal(yup.string),
    invoiceId: yup.string().nullable(),
    invoiceNumber: requireForFinal(yup.string),
    lineItems: yup
      .array()
      .of(
        yup
          .object({
            accountId: requireForFinal(yup.string),
            date: requireForFinal(yup.string),
            dateUntil: yup.string().nullable(),
            descriptions: yup
              .array(yup.object({ description: yup.string(), language: yup.string() }))
              .test('has-description', t('fieldIsRequired'), (value, ctx) => {
                if (ctx.options.context?.isDraft) return true;

                return (
                  !!value?.every((description) => !!description.description) && value.length === 3
                );
              }),
            divisionKey: yup.string().nullable(),
            divisionType: yup.string().nullable(),
            grossAmount: grossAmountSchema,
            isDefault: yup.boolean(),
            netAmount: yup
              .number()
              .nullable()
              .transform((value) => (Number.isNaN(value) ? 0 : value)),
            percentage: requireForFinal(yup.number).transform((value) =>
              Number.isNaN(value) ? 0 : value,
            ),
            shares: yup
              .array()
              .of(
                yup.object({
                  amount: yup
                    .number()
                    .nullable()
                    .transform((value) => (Number.isNaN(value) ? 0 : value)),
                  buildingRelationId: yup.string().nullable(),
                  percentage: yup
                    .number()
                    .nullable()
                    .transform((value) => (Number.isNaN(value) ? 0 : value)),
                  share: requireForFinal(yup.number)
                    .transform((value) => (Number.isNaN(value) ? 0 : value))
                    .max(
                      Number.MAX_SAFE_INTEGER,
                      t('errors:maxValueX', { val: Number.MAX_SAFE_INTEGER }),
                    ),
                  unitId: requireForFinal(yup.string),
                }),
              )
              .test(
                'dividedAmountsMustMatchLineAmount',
                'dividedAmountsMustMatchLineAmount',
                (shares, schema) => {
                  const {
                    parent,
                    options: { context },
                  } = schema;
                  if (context?.isDraft) return true;

                  if (
                    !shares?.length ||
                    parent.divisionKey ||
                    parent.divisionType === DivisionType.Percentage
                  ) {
                    return true;
                  }

                  const totalSharesAmount = sumBy(shares, 'amount');
                  return totalSharesAmount.toFixed(2) === (parent.grossAmount ?? 0).toFixed(2);
                },
              ),
            vatAmount: requireForFinal(yup.number).transform((value) =>
              Number.isNaN(value) ? 0 : value,
            ),
            vatCode: requireForFinal(yup.string),
          })
          .test('divisionIsValid', '', (lineItem, ctx) => {
            const isDraft = ctx.options.context?.isDraft;

            if (isDraft) return true;

            const { errors, isValid } = validateIndividualDivision(lineItem as LineItem);

            if (isValid) return true;

            return ctx.createError({
              message: errors[0],
            });
          })
          .test(
            'dateUntilAfterDate',
            t('errors:xCanNotBeBeforeY', {
              from: t('common:startDate').toLowerCase(),
              to: t('common:endDate'),
            }),
            (lineItem) => {
              const { date, dateUntil } = lineItem ?? {};
              if (!dateUntil || !date) return true;
              return dayjs(dateUntil).isSameOrAfter(dayjs(date), 'day');
            },
          ),
      )
      .test('percentage', t('percentageMustAddToHundred'), (lineItems, { parent }) => {
        if (parent.isDraft) return true;
        if (!parent.lineItems) return false;

        const percentageSum = sumBy(lineItems, (item) => item.percentage || 0);

        return Math.abs(percentageSum - 100) < 0.05;
      }),
    meterCode: yup.string().nullable(),
    octopusJournalKey: octopusJournalKeySchema,
    paymentMethod: requireForInvoice(yup.string),
    structuredReference: requireForInvoice(yup.string).test(
      'structuredCommunicationIsValid',
      t('invalidPaymentReference'),
      (value, ctx) => {
        const isDraft = ctx.options.context?.isDraft;
        const isCreditNote =
          ctx.options.context?.octopusJournalKey?.type === InboundHoaInvoiceType.CreditNote;
        if (!value || !canBeOGM(value) || isDraft || isCreditNote) return true;

        return isValidOGM(value);
      },
    ),
    supplierAccountNumber: requireForInvoice(yup.string).test(
      'mustBeValid',
      t('ChecksumNotNumber'),
      (value, ctx) => {
        const isDraft = ctx.options.context?.isDraft;
        const isCreditNote =
          ctx.options.context?.octopusJournalKey?.type === InboundHoaInvoiceType.CreditNote;
        if (!value || isDraft || isCreditNote) return true;

        return ibantools.isValidIBAN(value);
      },
    ),
    supplierBic: requireForInvoice(yup.string),
    supplierCompanyId: requireForFinal(yup.string),
  });

  return {
    enableReinitialize: true,
    initialTouched,
    initialValues,
    validationSchema,
  } as const;
};

export default useInvoiceValidationFormikContext;
